import React, { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client';

import { white } from '@crehana/ui/dist/styles/colors';
import { Container, Row, Col } from '@crehana/ui/dist/GridV2';
import TextTag from '@crehana/ui/dist/Tag/TextTag';
import { Clipboard } from '@crehana/icons/dist/pictograms';

import { notEmpty } from '@crehana/utils';

import GET_SKILL_TRACKS_QUERY from '../../graphql/skillTracksTrackDetailQuery.graphql';

import {
  skillTracksTrackDetailQuery,
  skillTracksTrackDetailQueryVariables,
} from '../../graphql/types/skillTracksTrackDetailQuery';

import {
  CoursesSectionWrapper,
  ContentWrapper,
  ContentIconWrapper,
} from './CoursesSection.styles';
import { CoursesSectionItem } from './components';
import VideoDialog from './components/VideoDialog';

import data from './CoursesSection.data';
import { CoursesSectionProps, Videotype } from './types';

const CoursesSection: React.FC<
  React.PropsWithChildren<CoursesSectionProps>
> = ({ slug }) => {
  const [itemId, setItemId] = useState<number | null>(1);
  const [courseData, setCourseData] = useState<Videotype | null>(null);

  const { data: skillTracksData } = useQuery<
    skillTracksTrackDetailQuery,
    skillTracksTrackDetailQueryVariables
  >(GET_SKILL_TRACKS_QUERY, {
    variables: {
      skillTrackSlug: slug,
    },
  });

  const courses =
    skillTracksData?.skillTrack?.courses
      ?.map(course => course)
      .filter(notEmpty) || [];
  const trackName = skillTracksData?.skillTrack?.name;
  const trackDescription = skillTracksData?.skillTrack?.description || '';

  const onOpen = useCallback(
    (id: number | null) => {
      setItemId(id);
    },
    [setItemId],
  );

  const onOpenVideo = useCallback(
    (videoData: Videotype) => {
      setCourseData(videoData);
    },
    [setCourseData],
  );

  return (
    <CoursesSectionWrapper className="pb-80">
      <Container>
        <Row>
          <Col
            offset={{
              lg: 1,
              md: 0,
              sm: 0,
              xs: 0,
            }}
            lg={10}
            xs={12}
          >
            <h1 className="font-h4 font-extrabold font-h3-md text-white mb-20 w-8/12 md:mb-24">
              {data.title}
            </h1>
            <ContentWrapper className="bg-white p-24 md:px-104 md:py-80">
              <div className="flex justify-end">
                <ContentIconWrapper className="bg-primary-light w-48 h-48 flex items-center justify-center -mt-40 md:-mt-96 md:w-104 md:h-104">
                  <Clipboard
                    className="w-24 h-24 md:w-48 md:h-48"
                    color={white}
                    secondaryColor={white}
                  />
                </ContentIconWrapper>
              </div>
              <div className="md:flex md:items-center mb-12">
                <h3 className="font-subtitle2 mb-8 mr-12 font-h4-md md:mb-0">
                  {trackName}
                </h3>
                <TextTag label={`${courses.length} Cursos`} />
              </div>
              <p className="font-body3 text-gray-main-dark-mode mb-40">
                {trackDescription}
              </p>
              {courses.map(course => {
                const isOpen = course.originalId === itemId;

                return (
                  <div key={course.originalId} className="mb-20">
                    <CoursesSectionItem
                      open={isOpen}
                      course={course}
                      onOpen={onOpen}
                      onOpenVideo={onOpenVideo}
                    />
                  </div>
                );
              })}
            </ContentWrapper>
          </Col>
        </Row>
      </Container>
      <VideoDialog
        courseData={courseData}
        onDismiss={() => {
          setCourseData(null);
        }}
      />
    </CoursesSectionWrapper>
  );
};

export default CoursesSection;
